import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import LoaderBox from "@entrilia/shared/components/LoaderBox";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import LockIcon from "@entrilia/shared/icons/LockIcon.tsx";
import { logError } from "@entrilia/shared/logging";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Button, Dialog, Divider, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import biApi from "../../../../../../../../api/biApi";
import { FormatType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { MeasureDescriptor } from "../../../../../../../../api/types/svTypes.ts";
import { selectMeasureSubgroups, selectMeasures } from "../../../../store/selectors/metaDataSelectors";
import { createMetaDataAction } from "../../../../store/utils/storeUtils";
import { AlertItem } from "../../../common/AlertItem";
import FormulaContainer from "./FormulaContainer";
import { useCustomMeasure } from "./hooks/useCustomMeasure";
import MainInfoComponent from "./mainInfo/MainInfoComponent";
import Properties from "./properties/Properties";
import SettingsComponent from "./settings/SettingsComponent";
import { buildCopyMeasureCaption } from "./utilities/buildCopyMeasureCaption";
import {
  isFormulaNumericOnly,
  isStandAloneFunctionWithNumbers,
  isValidFormulaStructure,
} from "./utilities/formulaValid";
import { convertToMeasure } from "./utilities/measureConverter";
import { getGroupSubgroups, resolveSubgroupId } from "./utilities/measureSubgroups";

const addCustomMeasure = withErrorHandling(biApi.addCustomMeasure);
const updateCustomMeasure = withErrorHandling(biApi.updateCustomMeasure);
const lockCustomMeasure = withErrorHandling(biApi.lockCustomMeasure);
const unlockCustomMeasure = withErrorHandling(biApi.unlockCustomMeasure);

interface Props {
  measure?: MeasureDescriptor;
  sourceMeasure?: MeasureDescriptor;
  defaultGroup?: string;
  onClose?: () => void;
}

export const CustomMeasureDialog = ({ measure, sourceMeasure, defaultGroup, onClose }: Props) => {
  const measures = useSelector(selectMeasures);
  const measureSubgroups = useSelector(selectMeasureSubgroups);

  const [baseMeasure] = useState<MeasureDescriptor | undefined>(() => {
    if (measure) {
      return measure;
    }
    if (!sourceMeasure) {
      return undefined;
    }
    return {
      ...sourceMeasure,
      caption: buildCopyMeasureCaption(
        sourceMeasure.caption,
        measures.map((m) => m.caption)
      ),
      subgroupId: resolveSubgroupId(sourceMeasure.subgroupId, getGroupSubgroups(measureSubgroups, sourceMeasure.group)),
      capabilities: { ...sourceMeasure.capabilities, isConfigurable: true },
    };
  });

  const state = useCustomMeasure({
    measure: baseMeasure,
    defaultGroup,
    measureFormat:
      baseMeasure?.configuration?.format?.formatType === FormatType.Numeric
        ? baseMeasure.configuration?.format
        : undefined,
  });
  const dispatch = useDispatch();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [currentMeasure, setCurrentMeasure] = useState<MeasureDescriptor | undefined>(measure);
  const [nameError, setNameError] = useState<string>();
  const [formulaValid, setFormulaValid] = useState<boolean>();
  const [formulaStructureError, setFormulaStructureError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingLock, setTogglingLock] = useState(false);
  const [savingError, setSavingError] = useState<string>();
  const [deletingError, setDeletingError] = useState<string>();
  const [isNewMeasure] = useState(measure === undefined);

  const isLocked = currentMeasure?.isLocked === true;

  useEffect(() => {
    if (!state.nodesCounter) {
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setFormulaStructureError(undefined);
      return;
    }
    let message: string | undefined;

    if (isStandAloneFunctionWithNumbers(state.nodesCounter)) {
      message = "Functions cannot contain any arithmetic operations.";
    } else if (!isValidFormulaStructure(state.nodesCounter)) {
      message =
        "Please enter a standalone function (e.g., XIRR(A)), an arithmetic operation with at least one variable (e.g., A/(B*-1)), or a single variable (e.g., A).";
    } else if (isFormulaNumericOnly(state.nodesCounter)) {
      message =
        "Only numbers are not allowed. Please enter a standalone function (e.g., XIRR(A)), an arithmetic operation with at least one variable (e.g., A/(B*-1)), or a single variable (e.g., A).";
    }
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setFormulaStructureError(message);
  }, [state.nodesCounter]);

  const saveMeasure = useCallback(async () => {
    const measureDescription = convertToMeasure(state, measure);
    setSaving(true);
    setSavingError(undefined);

    if (isNewMeasure) {
      const [addedMeasure, error] = await addCustomMeasure(measureDescription);
      setSaving(false);
      if (error) {
        logError(error, "[CustomMeasureDialog] addCustomMeasure");
        setSavingError(error.message);
        return;
      }
      dispatch(createMetaDataAction("addMeasure", addedMeasure));
      sendNotification(`The measure "${measureDescription.caption}" has been successfully saved.`);
      onClose?.call(null);
      return;
    }
    const [updatedMeasure, error] = await updateCustomMeasure(measure?.measureId ?? "", measureDescription);
    setSaving(false);
    if (error) {
      logError(error, "[CustomMeasureDialog] updateCustomMeasure");
      setSavingError(error.message);
      return;
    }
    dispatch(createMetaDataAction("updateMeasure", updatedMeasure));
    sendNotification(`The measure "${measureDescription.caption}" has been successfully updated.`);
    onClose?.call(null);

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isNewMeasure, state, onClose]);

  const onDeleteMeasure = useCallback(() => {
    if (measure === undefined) return;
    setDeleting(true);
    biApi
      .deleteCustomMeasure(measure.measureId ?? "")
      .then(() => {
        dispatch(createMetaDataAction("deleteMeasure", measure));
        sendNotification(`The measure "${measure.caption}" has been deleted.`);
        onClose?.call(null);
      })
      .catch(() => {
        setDeletingError("An error occurred while deleting the measure.");
      })
      .finally(() => setDeleting(false));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [measure?.name, onClose]);

  const handleToggleLock = useCallback(async () => {
    if (!currentMeasure) {
      return;
    }
    setTogglingLock(true);
    try {
      const id = currentMeasure.measureId ?? currentMeasure.name;
      const [data, error] = isLocked ? await unlockCustomMeasure(id) : await lockCustomMeasure(id);
      if (error || !data) {
        if (error) {
          logError(error, "[CustomMeasureDialog] toggleLock");
        }
        sendNotificationError(
          isLocked
            ? "An error occurred while unlocking the measure for editing."
            : "An error occurred while locking the measure for editing."
        );
      } else {
        setCurrentMeasure(data);
        dispatch(createMetaDataAction("updateMeasure", data));
        sendNotification(
          `The measure "${currentMeasure.caption}" has been ${isLocked ? "unlocked" : "locked"} for editing.`
        );
      }
    } finally {
      setTogglingLock(false);
    }
  }, [currentMeasure, isLocked, dispatch, sendNotification, sendNotificationError]);

  const formValid = useMemo(() => {
    return state.formulaValid && formulaValid === true && nameError === undefined && !formulaStructureError;
  }, [state.formulaValid, formulaValid, nameError, formulaStructureError]);

  return (
    <Dialog
      open={true}
      slotProps={{
        paper: {
          sx: {
            width: "100%",
            height: "100%",
            maxWidth: "1070px",
            maxHeight: "700px",
            overflow: "hidden",
          },
        },
      }}
    >
      <Grid container sx={{ alignItems: "center", py: 2, px: 3, width: "100%" }}>
        <Typography variant="h6">{isNewMeasure ? "New Measure" : "Edit Measure"}</Typography>
        <HorizontalFill />
        <CloseIconButton onClick={onClose} />
      </Grid>
      <Divider flexItem />
      {state.dataLoadingError || state.datasetError ? (
        <DataLoadingFailed text={state.dataLoadingError || state.datasetError} />
      ) : (
        <LoaderBox loading={state.loading}>
          <Stack sx={{ height: "100%", pointerEvents: isLocked ? "none" : "auto", opacity: isLocked ? 0.6 : 1 }}>
            <Grid container sx={{ flex: 1, flexWrap: "nowrap", width: "100%" }}>
              <Grid size={4.5} sx={{ display: "flex", flexDirection: "column", py: 2, pl: 3, pr: 2.5, gap: 3 }}>
                <MainInfoComponent state={state} measure={measure} onError={setNameError} />
                <SettingsComponent state={state} />
              </Grid>
              <Divider orientation="vertical" flexItem />
              <Grid size={7.8} sx={{ display: "flex", flexDirection: "column" }}>
                {savingError && (
                  <Grid sx={{ pt: 2, pb: 1.5, pl: 2.5, pr: 3, width: "100%" }}>
                    <AlertItem error={savingError} />
                  </Grid>
                )}
                {deletingError && (
                  <Grid sx={{ pt: 2, pb: 1.5, pl: 2.5, pr: 3, width: "100%" }}>
                    <AlertItem error={deletingError} />
                  </Grid>
                )}
                <FormulaContainer
                  state={state}
                  measure={baseMeasure}
                  editable
                  onValidated={setFormulaValid}
                  sx={{ py: 2, pl: 2.5, pr: 3 }}
                  formulaStructureError={formulaStructureError}
                />
                <Divider flexItem />
                <ScrollableFlexContainer>
                  <Properties state={state} editable={true} />
                </ScrollableFlexContainer>
              </Grid>
            </Grid>
          </Stack>
        </LoaderBox>
      )}
      <Divider flexItem />
      <Grid
        container
        sx={{ justifyContent: "space-between", alignItems: "center", py: 2, px: 3, gap: 1, width: "100%" }}
      >
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          {measure && !isNewMeasure && (
            <Button
              variant="outlined"
              color="error"
              disabled={saving || state.loading || isLocked}
              loading={deleting}
              onClick={onDeleteMeasure}
            >
              Delete
            </Button>
          )}
          {measure && !isNewMeasure && (
            <AuthorizedBox permissions={["ManageCustomMeasures"]}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleToggleLock}
                loading={togglingLock}
                startIcon={isLocked ? <LockOpenOutlinedIcon /> : <LockOutlinedIcon />}
              >
                {isLocked ? "Unlock Editing" : "Lock Editing"}
              </Button>
            </AuthorizedBox>
          )}
          {isLocked && currentMeasure?.modifiedBy && (
            <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
              <LockIcon color="primary" />
              <Typography variant="caption" color="textSecondary">
                Locked by
              </Typography>
              <Typography component="span">{currentMeasure.modifiedBy}</Typography>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <Button variant="text" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!formValid || deleting || isLocked}
            onClick={saveMeasure}
            loading={saving}
          >
            Save
          </Button>
        </Stack>
      </Grid>
    </Dialog>
  );
};

export default CustomMeasureDialog;
